import React, { useMemo, useState } from 'react';

import { commodityApis } from '@apis/next/commodityApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { Layout } from '@components/Layout';
import {
  ManagementFormContainer,
  ManagementFormLabel,
  ManagementFormInput,
} from '@components/NextForms/ManagementForm';
import { MultiImageUploadInput } from '@components/NextForms/MultiImageUploadInput';
import { QueryContainer } from '@components/QueryContainer';
import { CommodityDisplay } from '@components/utilities/Commodity/CommodityDisplay';
import { CommodityPhotoBrowser } from '@components/utilities/Commodity/CommodityPhotoBrowser';
import { Container } from '@components/utilities/Contact/Container';
import { VerticalSpace } from '@components/VerticalSpace';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  Commodity,
  CommodityActionStatus,
  CommodityForm,
  CommodityHelpers,
} from '@models/next/Commodity';
import { FileTypeHelper } from '@models/next/FileType';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { stringify } from 'query-string';
import { Link, useHistory } from 'react-router-dom';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    commodityInfo: '商品情報',
    create: '登録',
    title: '商品情報の新規登録',
    placeholders: {
      commodity_no: '例：152364789',
      name_en: '例：CHAIR',
      name_ja: '例：椅子',
      explanation: '編み方、形状など',
      memo: '参考HS CODEがあれば、教えてください。商品カテゴリーが分かれば、ここに記入してください。例えば、雑貨、機械、アパレルなど。',
      hs_code: 'D2D担当者記入欄',
    },
    localImagesHelperText:
      '＊商品の写真、絵型、カタログ、設計図、仕様書などをアップロードしてください。最大6枚まで追加できます。',
    createSuccess: '正常に登録されました。',
    editSuccess: '正常に更新されました。',
    deleteHint: '商品情報を削除しますか？復元するには再入力して頂きます。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  buttonContainer: {
    ...common.flexCenter,
    ...common.colSpanFull,
    columnGap: '20px',
  },
}));

const CommodityCreateEditForm: React.FC<
  CommodityCreateDetailsProps & {
    commodity?: Commodity;
  }
> = ({ type, id, commodity }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const form = useForm(CommodityForm, {
    initialOutput: commodity
      ? useMemo(() => CommodityHelpers.mapToForm(commodity), [commodity])
      : empty,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const queryClient = useQueryClient();

  const refresh = () =>
    queryClient.refetchQueries(commodityApis.getKeyByPath('get', '/commodity/:id'));

  const postCommodity = commodityApis.usePost('/commodity');
  const patchCommodity = commodityApis.usePatch('/commodity/:id', {
    params: {
      id: id!,
    },
  });

  const fileUpload = fileUploadApis.usePost('/file/upload');

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postCommodity, patchCommodity, fileUpload],
  });

  return (
    <form
      style={{ paddingBottom: 30 }}
      {...form.bindForm({
        async onSubmit(values) {
          if (type === 'create') {
            const { id } = await postCommodity.mutateAsync({
              ...values,
              images:
                values.local_images.length > 0
                  ? await fileUpload.mutateAsync({
                      file: values.local_images.map(({ file }) => file),
                    })
                  : [],
            });

            refresh();

            history.push({
              pathname: url('/utilities/Commodity/CommodityDetails'),
              search: stringify({
                action: 'createSuccess',
                id,
              }),
            });
          } else {
            await patchCommodity.mutateAsync({
              ...values,
              images: await FileTypeHelper.uploadLocalArrayToRemote(
                values.local_images,
                fileUpload.mutateAsync,
              ),
            });

            refresh();

            history.push({
              pathname: url('/utilities/Commodity/CommodityDetails'),
              search: stringify({
                action: 'editSuccess',
                id,
              }),
            });
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}

      <ManagementFormContainer mode="edit" containerProps={{ 'data-testid': 'Commodity' }}>
        <ManagementFormLabel field={fields.commodity_no} required />
        <ManagementFormInput
          field={fields.commodity_no}
          inputProps={{ placeholder: strings.placeholders.commodity_no }}
        />

        <ManagementFormLabel field={fields.name_en} required />
        <ManagementFormInput
          field={fields.name_en}
          inputProps={{ placeholder: strings.placeholders.name_en }}
        />

        <ManagementFormLabel field={fields.name_ja} />
        <ManagementFormInput
          field={fields.name_ja}
          inputProps={{ placeholder: strings.placeholders.name_ja }}
        />

        <ManagementFormLabel field={fields.url} />
        <ManagementFormInput field={fields.url} />

        <ManagementFormLabel field={fields.material} required />
        <ManagementFormInput field={fields.material} />

        <ManagementFormLabel field={fields.usage} required />
        <ManagementFormInput field={fields.usage} />

        <ManagementFormLabel field={fields.explanation} />
        <ManagementFormInput
          field={fields.explanation}
          multiline
          rows={3}
          inputProps={{ placeholder: strings.placeholders.explanation }}
        />

        <ManagementFormLabel field={fields.memo} />
        <ManagementFormInput
          field={fields.memo}
          multiline
          rows={3}
          inputProps={{ placeholder: strings.placeholders.memo }}
        />

        <ManagementFormLabel field={fields.local_images} />
        <MultiImageUploadInput
          field={fields.local_images}
          helperText={strings.localImagesHelperText}
        />

        <ManagementFormLabel field={fields.hs_code} disabled />
        <ManagementFormInput
          field={fields.hs_code}
          inputProps={{ placeholder: strings.placeholders.hs_code }}
          disabled
        />

        <div className={classes.buttonContainer}>
          <Button
            text={type === 'create' ? strings.create : formStrings.update}
            color="primary"
            size="small"
            type="submit"
            style={{ width: 100 }}
          />

          {type === 'details' && (
            <Link
              to={{
                pathname: url('/utilities/Commodity/CommodityDetails'),
                search: stringify({
                  id,
                }),
              }}
            >
              <Button text={formStrings.cancel} color="grey" size="small" style={{ width: 100 }} />
            </Link>
          )}
        </div>
      </ManagementFormContainer>
    </form>
  );
};

const CommodityDisplayForm: React.FC<
  CommodityCreateDetailsProps & {
    commodity: Commodity;
  }
> = ({ commodity }) => {
  const { id } = commodity;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [photoBrowserOpen, setPhotoBrowserOpen] = useState(false);

  const form = useForm(z.object({}));

  const { renderDialog, confirm } = useConfirmDialog();

  const deleteCommodity = commodityApis.useDelete('/commodity/:id', {
    params: {
      id,
    },
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [deleteCommodity] });

  const history = useHistory();

  return (
    <form
      style={{ paddingBottom: 30 }}
      {...form.bindForm({
        async onSubmit() {
          if (
            (await confirm({
              message: strings.deleteHint,
            })) === 'confirmed'
          ) {
            await deleteCommodity.mutateAsync(undefined);

            history.push(url('/utilities/Commodity/Commodities'));
          }
        },
      })}
    >
      {renderDialog()}
      {renderAlertsAndSpinnerModal()}

      <CommodityDisplay commodity={commodity} onPhotosPreview={() => setPhotoBrowserOpen(true)} />
      <CommodityPhotoBrowser
        open={photoBrowserOpen}
        urls={commodity.images}
        onClose={() => setPhotoBrowserOpen(false)}
      />

      <div style={{ height: 20 }} />

      <div className={classes.buttonContainer}>
        <Link
          to={{
            pathname: url('/utilities/Commodity/CommodityDetails'),
            search: stringify({
              id,
              action: 'edit',
            }),
          }}
        >
          <Button text={formStrings.edit} color="primary" size="small" style={{ width: 100 }} />
        </Link>

        <Button
          text={formStrings.delete}
          color="grey"
          size="small"
          style={{ width: 100 }}
          type="submit"
        />
      </div>
    </form>
  );
};

export interface CommodityCreateDetailsProps {
  type: 'create' | 'details';
  id?: number;
  action?: CommodityActionStatus;
}

export const CommodityCreateDetails: React.FC<CommodityCreateDetailsProps> = (props) => {
  const { type, id, action } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const commodityQuery = commodityApis.useGet(
    '/commodity/:id',
    {
      params: { id: id! },
    },
    {
      enabled: type === 'details',
    },
  );

  return (
    <Layout
      title={strings.title}
      breadcrumbs={[
        '/utilities/Dashboard',
        '/utilities/Commodity/Commodities',
        {
          text:
            type === 'details'
              ? commodityQuery.data?.name_en ?? strings.commodityInfo
              : strings.title,
        },
      ]}
    >
      <div className={classes.body}>
        <Container>
          <VerticalSpace height={32} />
          <FormTitle title={strings.commodityInfo} />

          {(action === 'createSuccess' || action === 'editSuccess') && (
            <Alert severity="success" open gutter>
              {strings[action]}
            </Alert>
          )}

          <QueryContainer queries={[commodityQuery]}>
            {type === 'create' && <CommodityCreateEditForm {...props} />}

            {type === 'details' &&
              (action === 'edit' ? (
                <CommodityCreateEditForm {...props} commodity={commodityQuery.data} />
              ) : (
                <CommodityDisplayForm {...props} commodity={commodityQuery.data!} />
              ))}
          </QueryContainer>
        </Container>
      </div>
    </Layout>
  );
};
